import { Campaign, useGetAllCampaigns } from "src/api/client-api/useCampaign";

type FilterOptions = {
    startFilter?: Pick<Campaign, "startDate">;
    endFilter?: Pick<Campaign, "endDate">;
    forceIsEqual?: boolean;
};

type Options = {
    filterOptions?: FilterOptions;
};
export const useCampaigns = (options?: Options) => {
    const { filterOptions } = options || {};
    const { campaigns } = useGetAllCampaigns({ orderByDate: true });

    const filteredCampaigns = _filterDates(campaigns, filterOptions);

    const isCampaignBetween = (
        value: Pick<Campaign, "startDate" | "endDate"> | undefined,
        forceIsEqual?: boolean,
    ): boolean => {
        const foundItems = _filterDates(filteredCampaigns, {
            startFilter: value,
            endFilter: value,
            forceIsEqual: forceIsEqual ?? true,
        });
        const isBetween = foundItems.length > 0;
        return isBetween;
    };

    return {
        campaigns: filteredCampaigns,
        isCampaignBetween,
    };
};

const getDate = (dateString: string): Date | null => {
    const date = new Date(dateString);
    const dateValue = date.valueOf();
    if (isNaN(dateValue)) return null;

    return date;
};

const _filterDates = (campaigns: Campaign[], filterOptions?: FilterOptions): Campaign[] => {
    if (filterOptions === undefined) {
        return campaigns;
    }
    const { startFilter, endFilter, forceIsEqual } = filterOptions;

    const startingFilter = startFilter === undefined ? undefined : startFilter.startDate;
    const endingFilter = endFilter === undefined ? undefined : endFilter.endDate;

    if (startFilter?.startDate === undefined && endFilter?.endDate === undefined) {
        return campaigns;
    }

    const filteredCampaigns = campaigns.flatMap((campaign) => {
        const isAfterStart =
            startingFilter === undefined ? true : (
                isCampaignBefore({
                    startDate: startingFilter,
                    endDate: campaign.startDate,
                    canBeEqual: forceIsEqual ?? false,
                })
            );

        const isBeforeEnd =
            endingFilter === undefined ? true : (
                isCampaignBefore({
                    startDate: campaign.endDate,
                    endDate: endingFilter,
                    canBeEqual: forceIsEqual ?? true,
                })
            );

        if (isAfterStart === false || isBeforeEnd === false) {
            return [];
        }
        return [campaign];
    });
    return filteredCampaigns;
};

export type CampaignComparisonOption = {
    startDate: string;
    endDate: string;
    canBeEqual?: boolean;
};
export const isCampaignBefore = (options: CampaignComparisonOption) => {
    const { startDate, endDate, canBeEqual = true } = options;

    const date1 = getDate(startDate);
    const date2 = getDate(endDate);

    if (date1 === null) return false;
    if (date2 === null) return true;

    return canBeEqual === true ? date1 <= date2 : date1 < date2;
};
